import type { QaReportFilters, ReportChoice, ReportFormat, ReportId, ReportKind, ReportParameters } from "./qaReportTypes";

type ReportDefinition = {
  label: string;
  kind: ReportKind;
  endpoint: string;
  dates: "none" | "single" | "range";
  requiresProduct?: boolean;
  supportsExcel?: boolean;
};

export const REPORTS: Record<ReportId, ReportDefinition> = {
  1: { label: "Receiving Details", kind: "receiving_details", endpoint: "generateGeneralReceivingDetailsReport", dates: "range" },
  2: { label: "Receiving Summary", kind: "receiving_summary", endpoint: "generateGeneralReceivingSummaryReport", dates: "range" },
  3: { label: "Reorder Limit Items", kind: "reorder_limit", endpoint: "reorderLimitReport", dates: "range" },
  4: { label: "Sales report by PLU", kind: "sales_by_plu", endpoint: "salesReportByPlu", dates: "range", requiresProduct: true },
  5: { label: "Deleted Cartons", kind: "deleted_cartons", endpoint: "deletedCartonsReport", dates: "range" },
  6: { label: "Inventory Valuation", kind: "inventory_valuation", endpoint: "inventoryValuationReport", dates: "single", supportsExcel: true },
  7: { label: "Open WIPs Report", kind: "open_wips", endpoint: "openWipsReport", dates: "none" },
  8: { label: "Flow Reports", kind: "inventory_flow", endpoint: "inventoryFlow", dates: "range", requiresProduct: true },
  9: { label: "Production Detail", kind: "production_details", endpoint: "productionDetailsReport", dates: "range", requiresProduct: true },
  10: { label: "Production Summary", kind: "production_summary", endpoint: "productionSummaryReport", dates: "range" },
};

const reportOrder: ReportId[] = [5, 6, 8, 7, 9, 10, 1, 2, 3, 4];
export const REPORT_OPTIONS: ReportChoice[] = reportOrder.map((value) => ({ value, label: REPORTS[value].label }));
export const NO_REPORT: ReportChoice = { value: -1, label: "Select report" };
export const ALL_PRODUCTS = { value: -1, label: "All products" };

export function defaultFilters(): QaReportFilters {
  return {
    class: { value: -1, label: "All classes" },
    category: { value: -1, label: "All categories" },
    product: ALL_PRODUCTS,
    supplier: { value: -1, label: "All suppliers" },
    startDate: null,
    endDate: null,
    lotNumber: "",
  };
}

export function validateReport(id: ReportId, filters: QaReportFilters, format: ReportFormat): string | null {
  const definition = REPORTS[id];
  if (definition.dates !== "none" && (!filters.startDate || (definition.dates === "range" && !filters.endDate))) {
    return "Please select a date range";
  }
  if (definition.requiresProduct && (!filters.product || filters.product.value < 1)) {
    return "Please select a product";
  }
  if (format === "xlsx" && !definition.supportsExcel) {
    return "Excel is available for the inventory valuation report.";
  }
  return null;
}

export function reportParameters(id: ReportId, filters: QaReportFilters, format: ReportFormat): ReportParameters {
  const serializeDate = (date: QaReportFilters["startDate"]): string | null => date instanceof Date ? date.toISOString() : date;
  return {
    ...filters,
    startDate: serializeDate(filters.startDate),
    endDate: serializeDate(REPORTS[id].dates === "single" ? filters.startDate : filters.endDate),
    ...(format === "xlsx" ? { format } : {}),
  };
}
